import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { forkJoin, of, Subject } from 'rxjs';
import { catchError, finalize, takeUntil } from 'rxjs/operators';
import { AppSharedModule } from 'app/app-shared.module';
import { AgrirouterService } from 'app/core/services/agrirouter.service';
import {
  JohnDeereService,
  JohnDeereStatus,
} from 'app/core/services/john-deere.service';
import { AgresSpinnerService } from 'app/shared/components/agres-spinner/agres-spinner.service';

const AGRIROUTER_WEBSITE_URL = 'https://www.agrirouter.com';
const JOHN_DEERE_OPERATIONS_CENTER_URL =
  'https://www.deere.com/en/technology-products/precision-ag-technology/data-management/operations-center/';

@Component({
  selector: 'app-data-exchange',
  standalone: true,
  imports: [AppSharedModule],
  templateUrl: './data-exchange.component.html',
  styleUrl: './data-exchange.component.scss',
})
export class DataExchangeComponent implements OnInit, OnDestroy {
  agrirouterConnected = false;
  johnDeereStatus: JohnDeereStatus = { connected: false };

  private destroy$ = new Subject<void>();

  constructor(
    private agrirouterService: AgrirouterService,
    private johnDeereService: JohnDeereService,
    private router: Router,
    private translateService: TranslateService,
    private agresSpinner: AgresSpinnerService,
  ) {}

  ngOnInit(): void {
    this.translateService.get('global.loading.user-data-spinner').subscribe((message: string) => {
      this.agresSpinner.toShow(message);
    });

    forkJoin({
      agrirouter: this.agrirouterService.checkOnboardStatus().pipe(catchError(() => of(null))),
      jd: this.johnDeereService.getStatus().pipe(catchError(() => of(null))),
    })
      .pipe(
        finalize(() => this.agresSpinner.toHide()),
        takeUntil(this.destroy$),
      )
      .subscribe(({ agrirouter, jd }) => {
        if (agrirouter) {
          this.agrirouterConnected = agrirouter.onboarded;
        }
        if (jd) {
          this.johnDeereStatus = jd;
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get agrirouterButtonTitle(): string {
    if (this.agrirouterConnected) {
      return 'data-exchange.manage';
    }
    return 'data-exchange.connect';
  }

  get agrirouterButtonIcon(): string {
    if (this.agrirouterConnected) {
      return 'agres-settings';
    }
    return 'agres-plus';
  }

  get johnDeereButtonTitle(): string {
    if (this.johnDeereStatus.connected) {
      return 'data-exchange.manage';
    }
    return 'data-exchange.connect';
  }

  get johnDeereButtonIcon(): string {
    if (this.johnDeereStatus.connected) {
      return 'agres-settings';
    }
    return 'agres-plus';
  }

  goToAgrirouter(): void {
    this.router.navigate(['/manage/connections/agrirouter']);
  }

  goToJohnDeere(): void {
    this.router.navigate(['/manage/connections/john-deere']);
  }

  learnMoreAgrirouter(): void {
    window.open(AGRIROUTER_WEBSITE_URL, '_blank', 'noopener,noreferrer');
  }

  learnMoreOC(): void {
    window.open(
      JOHN_DEERE_OPERATIONS_CENTER_URL,
      '_blank',
      'noopener,noreferrer',
    );
  }
}
